import { API_URL } from '@/config/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Swords,
  Clock,
  Coins,
  Trophy,
  Loader2,
  ArrowLeft,
  Check,
  X,
  Send,
  Trash2
} from "lucide-react";

export default function FriendsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [challenges, setChallenges] = useState({ incoming: [], outgoing: [] });
  const [searchUsername, setSearchUsername] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [challengeDialog, setChallengeDialog] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [challengeSettings, setChallengeSettings] = useState({
    time_control: "600",
    increment: "0",
    stake: "0"
  });

  // API_URL imported from config

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [friendsRes, requestsRes, challengesRes] = await Promise.all([
        fetch(`${API_URL}/api/friends`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/friends/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/challenges`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (friendsRes.ok) {
        const data = await friendsRes.json();
        setFriends(data.friends || []);
      }
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data);
      }
      if (challengesRes.ok) {
        const data = await challengesRes.json();
        setChallenges(data);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  const sendFriendRequest = async () => {
    if (!searchUsername.trim()) return;
    
    setSendingRequest(true);
    try {
      const res = await fetch(`${API_URL}/api/friends/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ username: searchUsername })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setSearchUsername("");
        loadData();
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSendingRequest(false);
    }
  };

  const handleFriendRequest = async (requestId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/friends/${action}/${requestId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        loadData();
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const removeFriend = async (friendId) => {
    try {
      const res = await fetch(`${API_URL}/api/friends/${friendId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(t("friends.removed"));
        loadData();
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const sendChallenge = async () => {
    if (!selectedFriend) return;
    
    try {
      const res = await fetch(`${API_URL}/api/challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          friend_id: selectedFriend.friend_id,
          time_control: parseInt(challengeSettings.time_control),
          increment: parseInt(challengeSettings.increment),
          stake: parseFloat(challengeSettings.stake)
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setChallengeDialog(false);
        loadData();
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleChallenge = async (challengeId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/challenges/${challengeId}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        if (action === "accept" && data.game_id) {
          navigate(`/game/${data.game_id}`);
        }
        loadData();
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const totalRequests = requests.incoming.length;
  const totalChallenges = challenges.incoming.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="friends-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/lobby")}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t("common.back")}
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black tracking-tight mb-2">
          {t("friends.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("friends.description")}
        </p>
      </div>

      {/* Add Friend */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {t("friends.addFriend")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder={t("friends.searchPlaceholder")}
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && sendFriendRequest()}
              data-testid="friend-search-input"
            />
            <Button 
              onClick={sendFriendRequest}
              disabled={sendingRequest || !searchUsername.trim()}
              data-testid="send-friend-request-btn"
            >
              {sendingRequest ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="friends">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="friends" className="relative">
            <Users className="h-4 w-4 mr-2" />
            {t("friends.myFriends")} ({friends.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="relative">
            <UserCheck className="h-4 w-4 mr-2" />
            {t("friends.requests")}
            {totalRequests > 0 && (
              <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-primary text-primary-foreground">
                {totalRequests}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="challenges" className="relative">
            <Swords className="h-4 w-4 mr-2" />
            {t("friends.challenges")}
            {totalChallenges > 0 && (
              <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground">
                {totalChallenges}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Friends List */}
        <TabsContent value="friends" className="mt-6">
          {friends.length === 0 ? (
            <div className="text-center py-10">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{t("friends.noFriends")}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {friends.map((friend) => (
                <Card key={friend.id} className="glass border-border/50">
                  <CardContent className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{friend.friend_username}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Trophy className="h-3 w-3" />
                            {friend.rating}
                          </span>
                          <span>{friend.games_played} {t("profile.gamesPlayed").toLowerCase()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedFriend(friend);
                          setChallengeDialog(true);
                        }}
                        data-testid={`challenge-${friend.friend_id}`}
                      >
                        <Swords className="h-4 w-4 mr-1" />
                        {t("friends.challenge")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFriend(friend.friend_id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Friend Requests */}
        <TabsContent value="requests" className="mt-6 space-y-6">
          {/* Incoming */}
          <div>
            <h3 className="font-semibold mb-3">{t("friends.incoming")}</h3>
            {requests.incoming.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t("friends.noIncoming")}</p>
            ) : (
              <div className="grid gap-3">
                {requests.incoming.map((req) => (
                  <Card key={req.id} className="glass border-border/50">
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <UserPlus className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-semibold">{req.from_username}</p>
                          <p className="text-sm text-muted-foreground">
                            {t("friends.wantsToBeYourFriend")}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleFriendRequest(req.id, "accept")}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleFriendRequest(req.id, "decline")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing */}
          <div>
            <h3 className="font-semibold mb-3">{t("friends.outgoing")}</h3>
            {requests.outgoing.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t("friends.noOutgoing")}</p>
            ) : (
              <div className="grid gap-3">
                {requests.outgoing.map((req) => (
                  <Card key={req.id} className="glass border-border/50 opacity-70">
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-semibold">{req.to_username}</p>
                          <p className="text-sm text-muted-foreground">
                            {t("friends.pendingRequest")}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Challenges */}
        <TabsContent value="challenges" className="mt-6 space-y-6">
          {/* Incoming Challenges */}
          <div>
            <h3 className="font-semibold mb-3">{t("friends.incomingChallenges")}</h3>
            {challenges.incoming.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t("friends.noChallenges")}</p>
            ) : (
              <div className="grid gap-3">
                {challenges.incoming.map((ch) => (
                  <Card key={ch.id} className="glass border-primary/50 glow-gold">
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <Swords className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">{ch.from_username}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {Math.floor(ch.time_control / 60)}m + {ch.increment}s
                            </span>
                            {ch.stake > 0 && (
                              <span className="flex items-center gap-1 text-primary">
                                <Coins className="h-3 w-3" />
                                {ch.stake} ₽
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="btn-gold"
                          onClick={() => handleChallenge(ch.id, "accept")}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          {t("friends.accept")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChallenge(ch.id, "decline")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing Challenges */}
          <div>
            <h3 className="font-semibold mb-3">{t("friends.outgoingChallenges")}</h3>
            {challenges.outgoing.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t("friends.noChallenges")}</p>
            ) : (
              <div className="grid gap-3">
                {challenges.outgoing.map((ch) => (
                  <Card key={ch.id} className="glass border-border/50 opacity-70">
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-semibold">{ch.to_username}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {Math.floor(ch.time_control / 60)}m + {ch.increment}s
                            </span>
                            {ch.stake > 0 && (
                              <span className="flex items-center gap-1">
                                <Coins className="h-3 w-3" />
                                {ch.stake} ₽
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline">{t("friends.waiting")}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Challenge Dialog */}
      <Dialog open={challengeDialog} onOpenChange={setChallengeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("friends.challengePlayer")}</DialogTitle>
            <DialogDescription>
              {t("friends.challengeDescription", { name: selectedFriend?.friend_username })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("game.timeControl")}</Label>
              <Select 
                value={challengeSettings.time_control}
                onValueChange={(v) => setChallengeSettings(s => ({...s, time_control: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="60">1 min (Bullet)</SelectItem>
                  <SelectItem value="180">3 min (Blitz)</SelectItem>
                  <SelectItem value="300">5 min (Blitz)</SelectItem>
                  <SelectItem value="600">10 min (Rapid)</SelectItem>
                  <SelectItem value="900">15 min (Rapid)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("game.increment")}</Label>
              <Select 
                value={challengeSettings.increment}
                onValueChange={(v) => setChallengeSettings(s => ({...s, increment: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">+0s</SelectItem>
                  <SelectItem value="1">+1s</SelectItem>
                  <SelectItem value="2">+2s</SelectItem>
                  <SelectItem value="5">+5s</SelectItem>
                  <SelectItem value="10">+10s</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("game.stake")} (₽)</Label>
              <Input
                type="number"
                min="0"
                value={challengeSettings.stake}
                onChange={(e) => setChallengeSettings(s => ({...s, stake: e.target.value}))}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {t("friends.stakeNote")}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setChallengeDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={sendChallenge} className="btn-gold">
              <Swords className="h-4 w-4 mr-2" />
              {t("friends.sendChallenge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
